/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  getGlobalProp,
  GlobalProp,
  GlobalProps
} from './GlobalPropsConfig';

/**
 * The `useGlobalProps` hook provides the API from which components can retrieve
 * `GlobalProp` values.
 *
 * If only a single `GlobalProp` is referenced, it will be returned directly:
 *
 * ```typescript
 *  import useGlobalProps from '@ion/dls/src/props/GlobalPropsConfig';
 *
 *  const SomeComponent = React.memo(() => {
 *   const name = useGlobalProps('Application.Name') as string;
 *   return (
 *     <div>
 *       <span>{name}</span>
 *     </div>
 *   );
 *  });
 * ```
 *
 * If multiple `GlobalProps` are referenced, they will be returned as an `Array`:
 *
 * ```typescript
 *  import useGlobalProps from '@ion/dls/src/props/GlobalPropsConfig';
 *
 *  const SomeComponent = React.memo(() => {
 *   const [name, version] = useGlobalProps(
 *     'Application.Name',
 *     'Application.Version'
 *   ) as [string, string];
 *   return (
 *     <div>
 *       <span>{name}</span>
 *       <span>{version}</span>
 *     </div>
 *   );
 *  });
 * ```
 *
 * If fallback values are needed, they can be assigned via standard `Array`
 * destructing default assignments:
 *
 * ```typescript
 *  const [
 *    name    = 'Fallback Name',
 *    version = 'Fallback Version'] = useGlobalProps(
 *    'Application.Name',
 *    'Application.Version'
 *  ) as [string, string];
 * ```
 *
 * @param   props One or more `GlobalProps` keys for which their values are to
 *          be returned.
 * @returns The values (if provided by the application, or defaults), for the
 *          `GlobalProps` keys specified by the `props` argument.
 */
export const useGlobalProps = <K extends GlobalProp>(
  ...props: K[]): GlobalProps[K][] | GlobalProps[K] => {
  if (props.length > 1) {
    return props.map(prop => getGlobalProp(prop));
  }
  return getGlobalProp(props[0]);
};
export default useGlobalProps;
